import type { UIRouterGlobals } from "@uirouter/angularjs";
import type { IScope } from "angular";
import { cloneDeep, get, isEqual } from "lodash";
import { finalize } from "rxjs/operators";

import type {
    ICommunicationConfiguration,
    ICommunicationInterface,
    IEnumService,
    IEquipmentDetails,
    IIPAddress,
    IResponseWithLoading,
    ISiteEquipmentWithCoverages,
} from "App/Modules/Models";
import { CommunicationInterfaceType, Protocol } from "App/Modules/Models";
import type { ISiteEquipmentRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve, IIpValidationService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class EditCommunicationConfigurationComponentController {
    public static $inject: string[] = [
        "$scope",
        "$uiRouterGlobals",
        "mmSiteEquipmentRepository",
        "mmEnumService",
        "irIpValidationService",
    ];

    public original: ICommunicationConfiguration;

    public edited: ICommunicationConfiguration;

    public siteId: string;

    public resolve: IFormContainerResolve<IEquipmentDetails, ISiteEquipmentWithCoverages>;

    public patchResponse: IResponseWithLoading<IEquipmentDetails>;

    public protocols: string[];

    public collapseExternalData = true;

    constructor(
        private readonly $scope: IScope,
        private readonly $uiRouterGlobals: UIRouterGlobals,
        private readonly siteEquipmentRepository: ISiteEquipmentRepository,
        private readonly enumService: IEnumService,
        private readonly ipValidationService: IIpValidationService,
    ) {}

    public $onInit() {
        this.protocols = this.enumService.enumToArray(Protocol).map((p) => p.value);
        if (this.resolve.bindings.equipment.communicationConfigurations.length > 0) {
            this.original = this.resolve.bindings.equipment.communicationConfigurations[0];
            this.edited = cloneDeep(this.original);
            if (!this.edited.ip) {
                this.edited.ip = {} as IIPAddress;
            }
            this.edited.ip.protocol = get(this.edited.ip, "protocol") || Protocol[Protocol.HTTPS];
            this.edited.ip.port =
                get(this.edited.ip, "port") || this.ipValidationService.getDefaultProtocolPort(this.edited.ip.protocol);
        } else {
            this.original = {} as ICommunicationConfiguration;

            const ipInterfaces = this.resolve.formData.supportedCommunicationInterfaces
                .filter(
                    (i: ICommunicationInterface) =>
                        i.type === CommunicationInterfaceType.WiredIP ||
                        i.type === CommunicationInterfaceType.WirelessIP,
                )
                .sort((a, b) => (a.type < b.type ? -1 : 1));
            const defaultType = ipInterfaces.length > 0 ? ipInterfaces[0].type : CommunicationInterfaceType.WiredIP;
            const defaultTypeName = ipInterfaces.length > 0 ? ipInterfaces[0].typeName : "WiredIp";
            const defaultProtocol = Protocol[Protocol.HTTPS];

            this.edited = {
                ip: {
                    protocol: defaultProtocol,
                    port: this.ipValidationService.getDefaultProtocolPort(defaultProtocol),
                },
                type: defaultType,
                typeName: defaultTypeName,
            } as ICommunicationConfiguration;
        }

        this.siteId = this.$uiRouterGlobals.params.siteId;
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public editCommunicationConfiguration() {
        const edited = cloneDeep(this.edited);
        const patchObject = {
            communicationConfigurations: [edited],
        } as IEquipmentDetails;

        if (!isEqual(this.original, this.edited)) {
            extendWithLoadingAndFail(
                this.siteEquipmentRepository.patchEquipment(this.siteId, this.resolve.bindings.id, patchObject),
            )
                .pipe(finalize(() => this.$scope.$applyAsync()))
                .subscribe(
                    (response: IResponseWithLoading<IEquipmentDetails>) => {
                        this.patchResponse = response;
                        if (!response.isLoading && response.data) {
                            this.resolve.options.onOk(response.data);
                        }
                    },
                    (error) => {
                        this.patchResponse = error;
                    },
                );
        } else {
            this.cancel();
        }
    }

    public changePort() {
        const { protocol } = this.edited.ip;
        if (protocol) {
            this.edited.ip.port = this.ipValidationService.getDefaultProtocolPort(this.edited.ip.protocol);
        }
    }

    public isPrivateIP(): boolean {
        return this.ipValidationService.isPrivateIP(this.edited.ip);
    }

    public isHttp(): boolean {
        return this.ipValidationService.isHttp(this.edited.ip);
    }

    public isWrongPortForProtocol(): boolean {
        if (this.edited.ip.port) {
            const isEditedPortWrong = this.ipValidationService.isWrongPortForProtocol(this.edited.ip);

            return isEditedPortWrong;
        }

        if (this.original.ip.port) {
            const isOriginalPortWrong = this.ipValidationService.isWrongPortForProtocol(this.original.ip);

            return isOriginalPortWrong;
        }

        return false;
    }

    public requiredFieldsMissingData() {
        return !this.edited.ip || !this.edited.ip.port;
    }
}
